import { defineStore } from "pinia";
import { computed, type ComputedRef, ref } from "vue";
import { ComponentState } from "@/models/componentState.ts";
import apiOrganisations from "@/functional/api/apiOrganisations.ts";
import useUserPermissions from "../functional/composables/useUserPermissions.ts";
import type { Organisation } from "@/models/organisation.model.ts";
import { useAuthStore } from "@/stores/useAuthStore.ts";
import type { ListedServicePlanModel } from "@/models/listedServicePlan.model.ts";
import apiServicePlan from "@/functional/api/apiServicePlan.ts";

export const useNavScope = defineStore("navScope", () => {
  // State
  const { isSuperAdmin } = useUserPermissions();
  const authStore = useAuthStore();
  const organisations = ref<Organisation[] | null>(null);
  const fetchOrganisationsCS = ref<ComponentState>(ComponentState.blank);
  const servicePlans = ref<ListedServicePlanModel[] | null>(null); // todo: include display name
  const fetchServicePlansCS = ref<ComponentState>(ComponentState.blank);

  const selectedOrganisation = ref<Organisation | null>(null);
  const selectedServicePlan = ref<ListedServicePlanModel | null>(null);

  // Getters
  const getOrganisations: ComputedRef<Organisation[] | null> = computed(
    () => organisations.value,
  );
  const getSelectedOrganisation = computed(() => selectedOrganisation.value);
  const getSelectedServicePlan = computed(() => selectedServicePlan.value);
  const getServicePlans = computed(() => servicePlans.value);
  const getAccessingUserOrganisation: ComputedRef<Organisation | null> =
    computed(() => {
      if (!authStore.currentUser) return null;

      return authStore.currentUser.organisation;
    });

  // Actions
  const fetchOrganisations = async () => {
    if (!isSuperAdmin()) {
      console.error(
        "Attempted to fetch organisations without super admin permissions",
      );
      fetchOrganisationsCS.value = ComponentState.erroring;
      return;
    }
    fetchOrganisationsCS.value = ComponentState.loading;
    return apiOrganisations
      .list()
      .then((r) => {
        organisations.value = r.organisations;
        fetchOrganisationsCS.value = ComponentState.ready;
        if (getAccessingUserOrganisation.value) {
          selectedOrganisation.value = getAccessingUserOrganisation.value;
        }
      })
      .catch((e) => {
        console.error(e);
        fetchOrganisationsCS.value = ComponentState.erroring;
      });
  };

  const fetchServicePlans = async () => {
    const currentUserId = authStore.currentUser?.publicId;
    if (!selectedOrganisation.value || !currentUserId) {
      console.error(
        "An error occurred while getting service plans: org id/current user id was not set",
      );
      fetchServicePlansCS.value = ComponentState.erroring;
      return;
    }

    fetchServicePlansCS.value = ComponentState.loading;
    await apiServicePlan
      .list(selectedOrganisation.value.publicId, currentUserId)
      .then((r) => {
        servicePlans.value = r;
        fetchServicePlansCS.value = ComponentState.ready;
      })
      .catch((e) => {
        console.error("Error getting service plans:", e);
        fetchServicePlansCS.value = ComponentState.erroring;
      });
  };

  const updateSelectedOrganisation = async (organisation: Organisation) => {
    selectedOrganisation.value = organisation;
    selectedServicePlan.value = null;
    await fetchServicePlans();
  };

  const updateSelectedServicePlan = async (
    servicePlan: ListedServicePlanModel | null,
  ) => {
    selectedServicePlan.value = servicePlan;
  };

  return {
    // Getters
    getOrganisations,
    getServicePlans,
    getSelectedOrganisation,
    getSelectedServicePlan,
    // Actions
    fetchOrganisations,
    fetchServicePlans,
    updateSelectedOrganisation,
    updateSelectedServicePlan,
  };
});
